import { Button } from '@hologram-dimension/button';
import { Chip } from '@hologram-dimension/chip';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import useBulkSimRename from 'api/bulkActions/useBulkSimRenameCall';
import useOpenSessions from 'api/legacy/session/useOpenSessions';
import useSimDetails from 'api/sims/useSimDetails';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import useSimDeviceOverview from 'api/sims/useSimDeviceOverview';
import useSimStatuses from 'api/sims/useSimStatuses';
import useTags from 'api/tags/useTags';
import clsx from 'clsx/lite';
import { Field, ManageSimButton, ProfileAndICCID, SimStatusChip } from 'components';
import IncludedDataField from 'components/IncludedDataField/IncludedDataField';
import SimDataUsage from 'components/SimDataUsage/SimDataUsage';
import SimNameEdit from 'components/SimNameEdit/SimNameEdit';
import useIsDeactivatedOldSim from 'hooks/SimDetails/useIsDeactivatedOldSim';
import useIsEuiccSim from 'hooks/SimDetails/useIsEuiccSim';
import useUserInfo from 'hooks/User/useUserInfo';
import TagManager from 'modals/TagManager/TagManager';
import { useCallback, useMemo, useState, type FC } from 'react';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import useOrganization from 'state/contexts/Organization/useOrganization';
import type {
  HandlersDeviceOverviewResponse,
  StructsGetTagsResponse,
  StructsTagResponseWithDetails,
} from 'types/__generated__/SimInventoryApi';
import type { HologramSimIdsMap, OnSimsModified, OpenModalCallback } from 'types/common';
import useSimUrl from 'utils/hooks/useSimUrl';
import alphabeticSortCompare from 'utils/strings/alphabeticSortCompare';
import ClickToCopy from 'components/ClickToCopy/ClickToCopy';
import styles from './SimDetailsHeader.module.css';

interface SimDetailsHeaderProps {
  hologramSimId?: HologramSimId;
  handleModalOpen: OpenModalCallback;
  onSimsModified: OnSimsModified;
  simDetailBaseUrl: string;
}

const SimDetailsHeader: FC<SimDetailsHeaderProps> = ({
  hologramSimId,
  handleModalOpen,
  onSimsModified,
  simDetailBaseUrl,
}) => {
  const { simDetails, isLoading: isSimLoading, error: simError } = useSimDetails(hologramSimId);
  const {
    deviceOverview,
    isLoading: isDeviceLoading,
    error: deviceError,
    mutate: mutateDeviceOverview,
  } = useSimDeviceOverview(hologramSimId);

  const {
    deviceDetails,
    isLoading: isDeviceDetailsLoading,
    error: deviceDetailsError,
  } = useSimDeviceDetails(hologramSimId);

  const {
    showEuiccInfo,
    isLoading: showEuiccInfoLoading,
    error: showEuiccInfoError,
  } = useIsEuiccSim(hologramSimId);

  const selectedSims = useMemo<HologramSimIdsMap>(
    () => (hologramSimId ? new Map([[hologramSimId, deviceDetails?.claimedDeviceId]]) : new Map()),
    [deviceDetails.claimedDeviceId, hologramSimId]
  );

  const { networkStatus, activeProfileIccid } = simDetails;

  const {
    isDeactivatedOldSim,
    isLoading: isDeactivatedOldSimLoading,
    error: isDeactivatedOldSimError,
  } = useIsDeactivatedOldSim({ hologramSimId });

  const { sendAnalyticsEvent } = useAnalytics();

  // TODO: Remove when we have real connected status
  const { isSuperAdmin } = useUserInfo();
  const organizationId = useOrganization();

  const { deviceIdsWithOpenSessions } = useOpenSessions(
    organizationId,
    isSuperAdmin || !!organizationId
  );
  const { networkStatusGroups } = useSimStatuses();

  const realNetworkState = useMemo(() => {
    if (
      deviceDetails.claimedDeviceId &&
      deviceIdsWithOpenSessions.has(deviceDetails.claimedDeviceId) &&
      !networkStatusGroups?.deactivated?.includes(networkStatus as any)
    ) {
      if (networkStatusGroups?.predeployment?.includes(networkStatus as any)) {
        return 'TEST-CONNECTED';
      }
      return 'CONNECTED';
    }
    return networkStatus;
  }, [
    deviceDetails.claimedDeviceId,
    deviceIdsWithOpenSessions,
    networkStatus,
    networkStatusGroups?.predeployment,
    networkStatusGroups?.deactivated,
  ]);

  // Device name management

  const [hoveringDeviceName, setHoveringDeviceName] = useState(false);
  const [deviceNameEditorOpen, setDeviceNameEditorOpen] = useState(false);

  const simNameApi = useBulkSimRename({ hologramSimIds: selectedSims, organizationId });

  const onSave = useCallback(
    async (newName: string) => {
      let result;
      try {
        result = await simNameApi(newName, false);
      } catch (err) {
        // TODO: Error management
        // eslint-disable-next-line no-console
        console.error(err);
        return;
      }

      if (result?.success) {
        const updatedResponse: HandlersDeviceOverviewResponse = {
          deviceOverview: { ...deviceOverview, deviceName: newName },
        };
        mutateDeviceOverview(updatedResponse, { revalidate: false });
      }

      // TODO: Error management

      setDeviceNameEditorOpen(false);
    },
    [deviceOverview, mutateDeviceOverview, simNameApi]
  );

  // Tag management

  const {
    tags: unsortedTags,
    isLoading: tagsLoading,
    error: tagsError,
    mutate: mutateTags,
  } = useTags({
    filters: { hologramSimIds: hologramSimId ? [hologramSimId] : [] },
    canLoad: !!hologramSimId,
  });

  const tags = useMemo(
    () => unsortedTags.sort((a, b) => alphabeticSortCompare(a.name ?? '', b.name ?? '')),
    [unsortedTags]
  );

  const [hoveringOnTags, setHoverOnTags] = useState(false);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);

  const handleTagsDone = useCallback(
    (updatedTags: Array<StructsTagResponseWithDetails>) => {
      const updatedResponse: StructsGetTagsResponse = {
        tags: updatedTags.filter((tag) => {
          if ((tag as any).selectedSimCount !== undefined) {
            return (tag as any).selectedSimCount > 0;
          }

          return !!tag.sims?.find((sim) => sim.id === hologramSimId);
        }),
      };
      mutateTags(updatedResponse, { revalidate: false });
    },
    [hologramSimId, mutateTags]
  );

  const url = useSimUrl({ simDetailBaseUrl, orgId: organizationId, simId: hologramSimId });

  const isLoading =
    isSimLoading ||
    isDeviceLoading ||
    showEuiccInfoLoading ||
    isDeactivatedOldSimLoading ||
    isDeviceDetailsLoading;
  const error =
    simError || deviceError || showEuiccInfoError || isDeactivatedOldSimError || deviceDetailsError;

  let contents;

  // TODO: Consider refactoring out tag list/editor.

  if (error) {
    // eslint-disable-next-line no-console
    console.error(`SIM Header API error: ${error}`);
    contents = (
      <PanelNotification
        className={styles.error}
        variant="error"
        label="We ran into an issue loading the SIM details header"
        actions={
          <Button
            variant="secondary"
            iconStart="Sync"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload page
          </Button>
        }
      >
        We will keep trying in the background or you can try to reload the page.
      </PanelNotification>
    );
  } else if (isLoading) {
    contents = <PanelNotification className={styles.loader}>Loading SIM...</PanelNotification>;
  } else {
    let tagsContent;

    if (tagsError) {
      tagsContent = 'Error loading tags';
    } else if (tagsLoading) {
      tagsContent = 'Loading...';
    } else {
      const leftoverTags = tags.length - 5;
      tagsContent = (
        <>
          <div className={styles.tags}>
            {tags.slice(0, 5).map((tag) => (
              <Chip
                key={tag.id}
                iconStart="Tag"
                size="small"
                type="interactive" // Must be interactive to get hover effect
                forceHover={hoveringOnTags}
              >
                {tag.name}
              </Chip>
            ))}
          </div>
          {leftoverTags > 0 && <div className={styles.moreTags}>+{leftoverTags} more</div>}
          {tags.length === 0 ? (
            <Button
              className={clsx(hoveringOnTags && 'static-demo-hover', styles.addATag)} // TODO: I would prefer to use the internal classnames
              variant="tertiary"
              onClick={() => {}} // Should do nothing
              iconStart="Tag"
              title="Manage tags"
            >
              Add a tag
            </Button>
          ) : (
            <Button
              className={clsx(hoveringOnTags && 'static-demo-hover', styles.pencil)} // TODO: I would prefer to use the internal classnames
              variant="tertiary"
              onClick={() => {}} // Should do nothing
              iconStart="Pencil"
              title="Manage tags"
            />
          )}
          {tagManagerOpen && (
            <TagManager
              sims={selectedSims}
              onClose={() => {
                setTagManagerOpen(false);
                if (sendAnalyticsEvent) sendAnalyticsEvent('Manage tags - Close');
              }}
              defaultOpen
              onDone={handleTagsDone}
              onSuccess={onSimsModified}
            />
          )}
        </>
      );
    }
    contents = (
      <>
        <div className={styles.headerRow}>
          <div className={styles.leftColumn}>
            <Field
              label="Name"
              title="Edit SIM name"
              valueClassname={styles.deviceNameFieldValue}
              value={
                deviceNameEditorOpen ? (
                  <SimNameEdit
                    existingName={deviceOverview.deviceName}
                    onSave={onSave}
                    onCancel={() => {
                      setDeviceNameEditorOpen(false);
                    }}
                  />
                ) : (
                  <div
                    className={clsx(
                      styles.deviceName,
                      hoveringDeviceName && styles.deviceNameHovered
                    )}
                  >
                    {deviceOverview.deviceName}
                    <Button
                      className={clsx(hoveringDeviceName && 'static-demo-hover', styles.pencil)} // TODO: I would prefer to use the internal classnames
                      variant="tertiary"
                      onClick={() => {}} // Should do nothing
                      iconStart="Pencil"
                      title="Edit SIM name"
                    />
                  </div>
                )
              }
              direction="column"
              className={styles.deviceNameField}
              onClick={() => {
                setDeviceNameEditorOpen(true);
              }}
              onMouseOver={() => {
                setHoveringDeviceName(true);
              }}
              onMouseLeave={() => {
                setHoveringDeviceName(false);
              }}
              labelId="nameLabel"
              aria-labelledby="nameLabel"
            />
            <Field
              label="Tags"
              valueClassname={styles.tagWrapper}
              value={tagsContent}
              direction="column"
              className={styles.tagsField}
              onClick={() => {
                setTagManagerOpen(true);
              }}
              onMouseOver={() => {
                setHoverOnTags(true);
              }}
              onMouseLeave={() => {
                setHoverOnTags(false);
              }}
              title="Manage tags"
              labelId="tagsLabel"
              aria-labelledby="tagsLabel"
            />
          </div>
          <div className={styles.rightColumn}>
            <ManageSimButton
              buttonText="Manage SIM"
              selectedSims={selectedSims}
              context="details"
              disabled={error}
              handleModalOpen={handleModalOpen}
              onSimsModified={onSimsModified}
              variant="primary"
              placement="bottom-end"
              className={styles.manageSimButton}
            />
          </div>
        </div>
        <div className={styles.headerRow}>
          <div className={styles.leftColumn}>
            <Field
              label="SIM status"
              value={<SimStatusChip networkState={realNetworkState} />}
              direction="column"
              className={styles.simStatus}
            />
            <Field
              label={showEuiccInfo ? 'Enabled profile ICCID' : 'Profile ICCID'}
              direction="column"
              value={
                activeProfileIccid && activeProfileIccid.length > 0 ? (
                  <ProfileAndICCID hologramSimId={hologramSimId} profileUrl={`${url}/profiles`} />
                ) : (
                  ''
                )
              }
            />
            {!isDeactivatedOldSim && (
              <>
                <Field
                  label="Data usage"
                  direction="column"
                  value={<SimDataUsage hologramSimId={hologramSimId} />}
                />
                <IncludedDataField hologramSimId={hologramSimId} />
              </>
            )}
            <Field
              label="SIM ID"
              direction="column"
              value={<ClickToCopy value={hologramSimId?.toString() ?? ''} label={hologramSimId} />}
              valueClassname={styles.numeric}
            />
            <Field
              label="Last known IMEI"
              direction="column"
              value={
                <div
                  className={clsx(!deviceOverview.imei && styles.noDataFoundValue, styles.numeric)}
                >
                  {deviceOverview.imei ? (
                    <ClickToCopy value={deviceOverview.imei} label={deviceOverview.imei} />
                  ) : (
                    'Not available'
                  )}
                </div>
              }
            />
          </div>
        </div>
      </>
    );
  }

  return <div className={styles.simHeader}>{contents}</div>;
};

export default SimDetailsHeader;
